import { HeatmapCell } from "@/redux/players/playersSlice";

type Props = {
  cells: HeatmapCell[];
  width?: number;
  height?: number;
  className?: string;
};

const VALUE_COLORS: Record<number, string> = {
  0: "#1F1F1F",
  1: "#1d4b6e",
  2: "#4781ad",
  3: "#96d0fa",
};

// Exact path definitions from previous svgs
const PITCH_PATHS = [
  { d: "M71.179 0H58.0036L58.3955 9.65355H72.3214L71.179 0Z" },
  { d: "M85.8786 0H72.7171L73.8595 9.65355H87.772L85.8786 0Z" },
  { d: "M20.2082 72H39.068L39.7492 54.5534H22.2804L20.2082 72Z" },
  { d: "M27.2218 0H14.1176L12.2248 9.65355H26.0753L27.2218 0Z" },
  { d: "M18.6707 72H0L3.4209 54.5534H20.7428L18.6707 72Z" },
  { d: "M3.72165 53.0196L6.63329 38.1702H22.6885L20.9249 53.0196H3.72165Z" },
  { d: "M96.2774 53.0196L93.365 38.1702H77.2342L78.9914 53.0196H96.2774Z" },
  { d: "M6.93367 36.6383L9.44345 23.8384H24.3906L22.8705 36.6383H6.93367Z" },
  { d: "M93.0645 36.6383L90.5541 23.8384H75.5381L77.0529 36.6383H93.0645Z" },
  { d: "M9.74389 22.3062L11.9244 11.1854H25.8934L24.5726 22.3062H9.74389Z" },
  { d: "M90.2535 22.3062L88.0724 11.1854H74.0408L75.3568 22.3062H90.2535Z" },
  { d: "M24.2263 38.1702L22.4626 53.0196H39.8091L40.3889 38.1702H24.2263Z" },
  { d: "M25.9285 23.8384L24.4082 36.6383H40.4487L40.9485 23.8384H25.9285Z" },
  { d: "M26.1105 22.3062H41.0083L41.4426 11.1854H27.4313L26.1105 22.3062Z" },
  { d: "M42.5397 22.3062H57.3802L56.9284 11.1854H42.9747L42.5397 22.3062Z" },
  { d: "M58.9092 22.3062H73.8187L72.5027 11.1854H58.4577L58.9092 22.3062Z" },
  { d: "M74.0001 23.8384H58.9715L59.4912 36.6383H75.5148L74.0001 23.8384Z" },
  { d: "M57.4424 23.8384H42.4798L41.9791 36.6383H57.9624L57.4424 23.8384Z" },
  { d: "M75.6961 38.1702H59.5534L60.1563 53.0196H77.4534L75.6961 38.1702Z" },
  { d: "M58.0247 38.1702H41.9192L41.3383 53.0196H58.6279L58.0247 38.1702Z" },
  { d: "M79.6995 72H60.9269L60.2186 54.5534H77.6349L79.6995 72Z" },
  { d: "M100 72H81.2376L79.173 54.5534H96.5782L100 72Z" },
  { d: "M59.399 72H40.5959L41.2783 54.5534H58.6903L59.399 72Z" },
  { d: "M28.7599 0H41.8793L41.5024 9.65355H27.6133L28.7599 0Z" },
  { d: "M43.4123 0H56.474L56.8662 9.65355H43.0347L43.4123 0Z" },
];

// Mapping from grid coordinates (x, y) to path indices
// Grid: x=0 (left) to x=4 (right), y=0 (top) to y=4 (bottom)
// Each grid cell maps to the primary path that represents that zone
// Based on analyzing path positions and the example.svg highlighted paths
const GRID_TO_PATHS: Record<string, number[]> = {
  // Row 0 (top) - y=0
  "0,0": [3],
  "1,0": [23],
  "2,0": [24],
  "3,0": [0],
  "4,0": [1],
  // Row 1 (top-middle) - y=1
  "0,1": [9],
  "1,1": [13],
  "2,1": [14],
  "3,1": [15],
  "4,1": [10],
  // Row 2 (middle) - y=2
  "0,2": [7],
  "1,2": [12],
  "2,2": [17],
  "3,2": [16],
  "4,2": [8],
  // Row 3 (bottom-middle) - y=3
  "0,3": [5],
  "1,3": [11],
  "2,3": [19],
  "3,3": [18],
  "4,3": [6],
  // Row 4 (bottom) - y=4
  "0,4": [4],
  "1,4": [2],
  "2,4": [22],
  "3,4": [20],
  "4,4": [21],
};

export const DynamicHeatmap = ({
  cells,
  width = 100,
  height = 72,
  className = "",
}: Props) => {
  // Map grid cells to values (clamp 0-3)
  const cellMap = new Map(
    cells.map((cell) => [
      `${cell.x},${cell.y}`,
      Math.max(0, Math.min(3, cell.value)),
    ]),
  );

  // Calculate path values: each path gets max value from grid cells that map to it
  const pathValues = new Array(PITCH_PATHS.length).fill(0);
  Object.entries(GRID_TO_PATHS).forEach(([gridKey, pathIndices]) => {
    const cellValue = cellMap.get(gridKey) ?? 0;
    pathIndices.forEach((pathIndex) => {
      if (pathIndex < 0 || pathIndex >= PITCH_PATHS.length) {
        console.error(`Invalid path index ${pathIndex} for grid ${gridKey}`);
        return;
      }
      pathValues[pathIndex] = Math.max(pathValues[pathIndex], cellValue);
    });
  });

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      {PITCH_PATHS.map((path, index) => (
        <path
          key={index}
          d={path.d}
          fill={VALUE_COLORS[pathValues[index]] || VALUE_COLORS[0]}
        />
      ))}
    </svg>
  );
};
